/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `VirtualEvents` for the Caintra Connect frontend.
 * @module pages/VirtualEvents
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/VirtualEvents.module.css";

import { generateAssetUrl, truncateText } from "../utils/common.js";
import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown";
import PageSelector from "../components/PageSelector";

import { CircularProgress, Backdrop } from "@mui/material";

import { use, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  getVirtualEventsPageData,
  showEventCreatedSuccess,
} from "../logic/virtualEvents";
import { isPastEvent } from "../utils/pastEventsAccess.js";
import {
  canManageVirtualEvents,
  canPerformEventListActions,
} from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

import Caintra_Logo_03 from "../assets/Caintra_Logo_03.png";
import EventBanner from "../assets/EventBanner.png";

import { ReactComponent as CalendarIcon } from "../assets/icons/Calendar_Today.svg";
import { ReactComponent as EditIcon } from "../assets/icons/Edit.svg";

import { ReactComponent as TitleIcon } from "../assets/icons/BusinessEvents/TitleIcon.svg";
import { ReactComponent as AddIcon } from "../assets/icons/BusinessEvents/AddIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/BusinessEvents/CheckCircleIcon.svg";
import AssetImage from "../components/AssetImage";
import { DEFAULT_RESULTS_LIMIT } from "../utils/resultsLimitOptions.js";

const VirtualEvents = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePermissions, permissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageVirtualEvents);

  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [successMessageVariant, setSuccessMessageVariant] = useState(null);

  const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1440;
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;

  const getDevice = () => {
    if (isSmallMobile) {
      return "smallMobile";
    }
    if (isMediumMobile) {
      return "mediumMobile";
    }
    if (isLaptop) {
      return "laptop";
    }
    if (isTablet) {
      return "tablet";
    }
    return "desktop";
  };

  const getSearchBarWidth = (device) => {
    if (device === "laptop") {
      return "80.2%";
    }
    if (device === "tablet") {
      return "73%";
    }
    if (device === "mediumMobile") {
      return "90%";
    }
    if (device === "smallMobile") {
      return "75%";
    }
    return "86%";
  };

  useEffect(() => {
    if (!allowed) {
      return;
    }
    const initializeData = async () => {
      try {
        await fetchData();
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        setIsLoading(false);
      }
    };

    setIsLoading(true);
    initializeData();
  }, [allowed]);

  useEffect(() => {
    if (location.state?.eventCreated) {
      setSuccessMessageVariant("created");
      showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.eventUpdated) {
      setSuccessMessageVariant("updated");
      showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      // No navigation state to handle.
    }
  }, [location.state, navigate, location.pathname]);

  const fetchData = async () => {
    try {
      const events = await getVirtualEventsPageData(
        localStorage.getItem("userId"),
        changePermissions,
        navigate,
        setUser,
      );
      setEventsData(events);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
  };

  const filteredEventsList = eventsData?.filter((event) =>
    event.nombre
      ? event.nombre.toLowerCase().includes(searchQuery.toLowerCase())
      : false,
  );

  const handlePageChange = (direction) => {
    if (
      direction === "next" &&
      currentPage < Math.ceil(filteredEventsList.length / resultsLimit)
    ) {
      setCurrentPage((prevPage) => prevPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  const paginatedEventsList = filteredEventsList.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );

  const canManage = canPerformEventListActions(
    user,
    permissions,
    canManageVirtualEvents,
  );

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>{t("pages.virtualEvents.header")}</h1>
          </div>
          <div className={styles.searchSection}>
            <SearchBar
              placeholder={
                isSmallMobile || isMediumMobile
                  ? t("pages.virtualEvents.mobileSearchText")
                  : t("pages.virtualEvents.searchText")
              }
              value={searchQuery}
              onChange={handleSearch}
              width={getSearchBarWidth(getDevice())}
            />
            {canManage && (
              <div
                className={styles.newRequirementButton}
                onClick={() =>
                  navigate("/virtualEventForm", {
                    state: { isEditMode: false },
                  })
                }
              >
                <AddIcon />
                {!isSmallMobile && !isMediumMobile ? (
                  <span>{t("pages.virtualEvents.createEvent")}</span>
                ) : (
                  ""
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.eventsList}>
        {paginatedEventsList.length === 0 && (
          <div className={styles.noResults}>
            <p>{t("pages.virtualEvents.noResults")}</p>
          </div>
        )}
        {paginatedEventsList.length > 0 &&
          paginatedEventsList.map((event, index) => (
            <div key={event._id || index} className={styles.eventCard}>
              <AssetImage
                className={styles.eventImage}
                src={
                  event.imagenBanner
                    ? generateAssetUrl(event.imagenBanner)
                    : EventBanner
                }
                alt=""
              />
              <div className={styles.eventInfo}>
                <div className={styles.eventHeader}>
                  <div className={styles.eventHeaderTitle}>
                    <div className={styles.messageItemAvatar}>
                      <img
                        className={styles.avatar}
                        src={Caintra_Logo_03}
                        alt=""
                      />
                    </div>
                    <span className={styles.eventName}>{event.nombre}</span>
                  </div>
                  {canManage && !isPastEvent(event) && (
                    <div className={styles.eventHeaderActions}>
                      <div
                        className={styles.buttonContainer}
                        onClick={() =>
                          navigate("/virtualEventForm", {
                            state: {
                              isEditMode: true,
                              eventData: event,
                            },
                          })
                        }
                      >
                        <EditIcon className={styles.editButtonIcon} />
                        <button type="button" className={styles.editButton}>
                          {t("pages.virtualEvents.editEvent")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.eventDetails}>
                  <div className={styles.eventDates}>
                    <div className={styles.datesRow}>
                      <div className={styles.eventDate}>
                        <span className={styles.dayOfWeek}>
                          {dayjs(event.fechaEvento).utc().format("dddd")}
                        </span>
                        <span className={styles.day}>
                          {dayjs(event.fechaEvento).utc().format("DD")}
                        </span>
                        <span className={styles.monthAndYear}>
                          {dayjs(event.fechaEvento).utc().format("MMMM")}
                          {", "}
                          {dayjs(event.fechaEvento).utc().format("YYYY")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.eventDescriptionContainer}>
                    <span className={styles.eventDescription}>
                      {truncateText(event.descripcion, 50)}
                    </span>
                  </div>
                </div>
                <div className={styles.eventAdditionalDetails}>
                  {event.fechaLimiteRegistro && (
                    <div className={styles.eventColumn}>
                      <div className={styles.eventColumnItem}>
                        <CalendarIcon className={styles.salesIcon} />
                        <label className={styles.eventLabel}>
                          {t("pages.virtualEvents.dueDateRegistration")}
                        </label>
                      </div>
                      <span className={styles.eventValue}>
                        {dayjs(event.fechaLimiteRegistro).utc().format("LLL")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className={styles.footer}>
        <ResultsDropdown onResultsLimitChange={handleResultsLimitChange} />
        <PageSelector
          currentPage={currentPage}
          totalPages={Math.ceil(filteredEventsList.length / resultsLimit)}
          onPageChange={handlePageChange}
        />
      </div>

      {showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${
            isSlidingUp ? styles.slideUp : ""
          }`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {t(
            successMessageVariant === "updated"
              ? "pages.virtualEvents.updateMessage"
              : "pages.virtualEvents.creationMessage",
          )}
        </div>
      )}

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default VirtualEvents;
